import { type ReactNode, useId, useRef } from "react";
import type { View } from "@common/types/views/views";
import { Button } from "@design-system/Button/Button";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { SettingsPanel } from "@design-system/SettingsPanel/SettingsPanel.lib";
import {
    type ColumnSettingsConfig,
    ColumnSettingsList,
    type ColumnSettingsListCopy,
} from "@design-system/ViewToolbar/ColumnSettingsList";
import { MenuIcon } from "@icons";
import type {
    BoardDisplayChange,
    BoardDisplayOption,
    BoardViewState,
} from "./Board.types";
import { BoardDisplaySelect } from "./BoardDisplaySelect";
import {
    BoardSortDirection,
    type BoardSortDirectionValue,
} from "./BoardSortDirection";

const NO_GROUP_VALUE = "__no-group__";

const CARD_FIELDS_COPY: ColumnSettingsListCopy = {
    shownTitle: "Shown on cards",
    hiddenTitle: "Hidden on cards",
    noneShown: "No fields on cards",
    noneHidden: "All fields are shown",
};

/**
 * The card fields as a list view's columns: the cards show the shown columns
 * offered here, in the columns' order, so editing them edits the list too.
 */
export interface BoardCardFieldColumns {
    /** Every column of the list view, in order. */
    columns: View["columns"];
    /** The columns a card can show, by id, named as the list names them. */
    columnConfigs: Readonly<Record<string, ColumnSettingsConfig>>;
    onColumnsChange: (columns: View["columns"]) => void;
    /** Fields whose place on the card is fixed: listed first, with no drag handle. */
    fixedColumnIds?: ReadonlySet<string>;
}

export interface BoardDisplayPopoverProps {
    value: BoardViewState;
    /**
     * Only the settings that changed, so an owner applying it later merges
     * it into its latest state (collapse changes made meanwhile included).
     */
    onChange: (change: BoardDisplayChange) => void;
    /** Without it there is no Columns by select: the surface picks columns elsewhere. */
    columnByOptions?: readonly BoardDisplayOption[];
    /**
     * Properties to group lanes by; "None" comes first on its own. Values
     * shared with `columnByOptions` name the same property, so the one the
     * columns use (`value.columnBy`) is disabled here.
     */
    groupByOptions: readonly BoardDisplayOption[];
    /** The lane select's label. */
    groupByLabel?: string;
    sortOptions: readonly BoardDisplayOption[];
    /** Turns Sort off and says why (a manual order is on, for example). */
    sortDisabledReason?: string;
    /** With `onSortDirectionChange`, shows Ascending and Descending beside Sort. */
    sortDirection?: BoardSortDirectionValue;
    onSortDirectionChange?: (direction: BoardSortDirectionValue) => void;
    /** Turns the direction off and says why (a sort with a fixed order). */
    sortDirectionDisabledReason?: string;
    /** Optional card fields, as List's column settings rows; always-visible parts don't belong here. */
    cardFields: BoardCardFieldColumns;
    /** Trigger name and tooltip. */
    triggerLabel?: string;
    triggerIcon?: ReactNode;
    /** Match the toolbar the trigger sits in. Defaults to primary, sm. */
    triggerButtonProps?: Pick<TriggerButtonProps, "variant" | "size">;
}

/**
 * Toolbar icon button opening a board's display settings: what the columns
 * and lanes show, the sort, the card fields (List's column settings rows,
 * with an eye to show or hide each and a handle to reorder those whose
 * place follows the order) and whether
 * empty columns show. Edits a `BoardViewState` the surface owns and applies.
 */
export const BoardDisplayPopover = ({
    value,
    onChange,
    columnByOptions,
    groupByOptions,
    groupByLabel = "Group by",
    sortOptions,
    sortDisabledReason,
    sortDirection,
    onSortDirectionChange,
    sortDirectionDisabledReason,
    cardFields,
    triggerLabel = "Display options",
    triggerIcon = <MenuIcon />,
    triggerButtonProps,
}: Readonly<BoardDisplayPopoverProps>) => {
    const directionReasonId = useId();
    const scrollerRef = useRef<HTMLDivElement>(null);

    const groupByItems = [
        { value: NO_GROUP_VALUE, label: "None" },
        ...groupByOptions.map((option) => ({
            ...option,
            disabled: option.value === value.columnBy,
            disabledReason: `Columns already show ${option.label}`,
        })),
    ];

    const hasDirection =
        sortDirection !== undefined && onSortDirectionChange !== undefined;

    return (
        <SettingsPanel.Root
            trigger={
                <Button
                    variant="primary"
                    size="sm"
                    {...triggerButtonProps}
                    iconOnly
                    aria-label={triggerLabel}
                >
                    {triggerIcon}
                </Button>
            }
            triggerTooltip={triggerLabel}
            scrollContainerRef={scrollerRef}
        >
            <SettingsPanel.Section>
                {columnByOptions !== undefined && (
                    <BoardDisplaySelect
                        label="Columns by"
                        items={columnByOptions}
                        value={value.columnBy}
                        onValueChange={(columnBy) =>
                            onChange({
                                columnBy,
                                groupBy:
                                    value.groupBy === columnBy
                                        ? null
                                        : value.groupBy,
                            })
                        }
                    />
                )}
                <BoardDisplaySelect
                    label={groupByLabel}
                    items={groupByItems}
                    value={value.groupBy ?? NO_GROUP_VALUE}
                    onValueChange={(groupBy) =>
                        onChange({
                            groupBy:
                                groupBy === NO_GROUP_VALUE ? null : groupBy,
                        })
                    }
                />
                <BoardDisplaySelect
                    label="Sort"
                    items={sortOptions}
                    value={value.sort}
                    onValueChange={(sort) => onChange({ sort })}
                    disabledReason={sortDisabledReason}
                    trailing={
                        hasDirection && (
                            <BoardSortDirection
                                value={sortDirection}
                                onValueChange={onSortDirectionChange}
                                disabledReason={sortDirectionDisabledReason}
                                reasonId={directionReasonId}
                            />
                        )
                    }
                    trailingHint={
                        hasDirection &&
                        sortDirectionDisabledReason !== undefined
                            ? {
                                  id: directionReasonId,
                                  text: sortDirectionDisabledReason,
                              }
                            : undefined
                    }
                />
            </SettingsPanel.Section>
            <SettingsPanel.Separator />
            <ColumnSettingsList
                {...cardFields}
                copy={CARD_FIELDS_COPY}
                scrollContainerRef={scrollerRef}
            />
            <SettingsPanel.Separator />
            <SettingsPanel.ToggleRow
                label="Show empty columns"
                checked={value.isEmptyColumnsShown}
                onCheckedChange={(isEmptyColumnsShown) =>
                    onChange({ isEmptyColumnsShown })
                }
            />
        </SettingsPanel.Root>
    );
};
